import { useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { regionState } from '../store/common-atoms';
import { foldersByRegionState, fetchFoldersForRegion } from '../store/folders-atoms';
import { getRegion } from '../utilities/gdpr-utils';

// Folders are scoped server-side to the region a request is served from. Caching
// per region here means switching regions fetches once and switching back is
// instant, with no consumer-owned refetch effect needed.
export const useCurrentRegionFolders = () => {
  // regionState is null until AppSkeleton's boot effect seeds it - fall back to
  // getRegion() (same sessionStorage key) so a consumer that renders before that
  // effect fires still resolves a real region instead of getting stuck on [].
  const region = useAtomValue(regionState) ?? getRegion();
  const [state, setState] = useAtom(foldersByRegionState);

  const { folders = [], loading = false, fetched = false, error = false } = state[region] || {};

  useEffect(() => {
    if (!region || fetched) return;
    setState(prev => ({ ...prev, [region]: { ...prev[region], loading: true } }));

    fetchFoldersForRegion(region)
      .then(result => {
        setState(prev => ({ ...prev, [region]: { folders: result, loading: false, fetched: true, error: false } }));
      })
      .catch(() => {
        // fetched:true here too - otherwise every remount (deps are [region, fetched])
        // refires the fetch forever with no backoff. error:true lets callers show a
        // failed state instead of a spinner stuck indistinguishable from "still loading".
        setState(prev => ({
          ...prev,
          [region]: { folders: prev[region]?.folders ?? [], loading: false, fetched: true, error: true }
        }));
      });
    // fetchFoldersForRegion dedupes concurrent calls for the same region at module
    // scope, so it's safe for every mounted consumer's effect to call it unguarded.
  }, [region, fetched]);

  // isFetched distinguishes "haven't fetched yet, folders is just the empty default"
  // from "fetched, and this region genuinely has zero folders" - callers that need to
  // react to the real list (e.g. pruning a stale selection) must gate on this, not on
  // folders.length, or they'll act on the placeholder value before the fetch resolves.
  return { folders, isLoading: loading, isFetched: fetched, isError: error };
};
